/**
 * SaaS Admin — X (Twitter) Global Control
 * Lets the SaaS super-admin (phone 9948303060) toggle the platform-wide
 * X listener on/off without redeploying, and see a per-tenant summary
 * (active / trial / has bearer / keywords count).
 *
 * Backend:
 *   GET  /api/twitter/admin/global    → { enabled, source, tenants[] }
 *   PUT  /api/twitter/admin/global    → { enabled }
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Twitter, Power, Loader2, RefreshCcw, CheckCircle2, AlertTriangle,
  Activity, Beaker, ShieldOff, ShieldCheck,
} from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import SaaSAdminLayout from "../layouts/SaaSAdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SaaSTwitterAdmin() {
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [data, setData] = useState(null);

  const headers = {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Content-Type": "application/json",
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/twitter/admin/global`, { headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      toast.error(e.message || "Failed to load");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const toggleGlobal = async (enabled) => {
    setToggling(true);
    try {
      const r = await fetch(`${API}/twitter/admin/global`, {
        method: "PUT", headers, body: JSON.stringify({ enabled }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setData((d) => d ? { ...d, ...j } : j);
      toast.success(
        enabled
          ? "X listener ENABLED platform-wide — next cycle will poll all active tenants"
          : "X listener DISABLED platform-wide — all tenants paused"
      );
    } catch (e) {
      toast.error(e.message);
    }
    setToggling(false);
  };

  if (loading) {
    return (
      <SaaSAdminLayout pageTitle="X (Twitter) Global Control">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-sky-600" />
        </div>
      </SaaSAdminLayout>
    );
  }

  const enabled = !!data?.enabled;
  const tenants = data?.tenants || [];
  const activeTenants = tenants.filter((t) => t.x_active).length;
  const trialTenants = tenants.filter((t) => t.x_trial_mode).length;
  const withBearer = tenants.filter((t) => t.has_x_bearer_token).length;

  return (
    <SaaSAdminLayout
      pageTitle="X (Twitter) Global Control"
      pageSubtitle="Platform-wide kill-switch + per-tenant overview"
    >
      <div className="space-y-6 max-w-5xl" data-testid="saas-twitter-admin">
        {/* ────────────── Master switch ────────────── */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Power className="w-4 h-4 text-sky-600" /> Master switch
            </CardTitle>
            <CardDescription>
              When OFF, no tenants are polled regardless of their individual
              settings. Takes effect on the next listener cycle (within
              ~1 minute). No redeploy needed.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-4">
            <Switch
              checked={enabled}
              disabled={toggling}
              onCheckedChange={toggleGlobal}
              data-testid="global-toggle"
            />
            <div className="flex items-center gap-2">
              {enabled ? (
                <Badge className="bg-emerald-100 text-emerald-800 border-0">
                  <Activity className="w-3 h-3 mr-1" /> Globally ON
                </Badge>
              ) : (
                <Badge className="bg-slate-100 text-slate-700 border-0">
                  <ShieldOff className="w-3 h-3 mr-1" /> Globally OFF
                </Badge>
              )}
              <span className="text-[11px] text-gray-500">
                source: <code className="bg-slate-100 px-1 rounded">{data?.source}</code>
              </span>
            </div>
            {toggling && <Loader2 className="w-4 h-4 animate-spin text-sky-600" />}
            <Button
              variant="outline" size="sm" onClick={load}
              data-testid="refresh-btn"
            >
              <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Refresh
            </Button>
          </CardContent>
        </Card>

        {/* ────────────── KPIs ────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat
            label="Total tenants"
            value={data?.tenant_count || 0}
            icon={<Twitter className="w-4 h-4 text-sky-500" />}
          />
          <Stat
            label="X active"
            value={activeTenants}
            tone="emerald"
            icon={<CheckCircle2 className="w-4 h-4" />}
          />
          <Stat
            label="In trial"
            value={trialTenants}
            tone="violet"
            icon={<Beaker className="w-4 h-4" />}
          />
          <Stat
            label="With bearer token"
            value={withBearer}
            tone="sky"
            icon={<ShieldCheck className="w-4 h-4" />}
          />
        </div>

        {/* ────────────── Tenant table ────────────── */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Per-tenant overview</CardTitle>
            <CardDescription>
              Snapshot of each tenant&apos;s X configuration. Tenants manage
              their own settings (keywords, bearer token, trial mode) from
              their dashboard.
            </CardDescription>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="tenant-table">
              <thead className="text-left text-[11px] uppercase tracking-wider text-gray-500 border-b">
                <tr>
                  <th className="px-3 py-2">Tenant</th>
                  <th className="px-3 py-2">Phone</th>
                  <th className="px-3 py-2">Active</th>
                  <th className="px-3 py-2">Trial</th>
                  <th className="px-3 py-2">Bearer</th>
                  <th className="px-3 py-2"># Keywords</th>
                  <th className="px-3 py-2">Cap/day</th>
                </tr>
              </thead>
              <tbody>
                {tenants.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-400">
                    No tenants found.
                  </td></tr>
                )}
                {tenants.map((t) => (
                  <tr key={t.id} className="border-b border-gray-100 hover:bg-sky-50/40" data-testid={`tenant-row-${t.id}`}>
                    <td className="px-3 py-2 font-medium text-gray-800">{t.name}</td>
                    <td className="px-3 py-2 text-gray-600 text-xs">{t.phone || "—"}</td>
                    <td className="px-3 py-2">
                      {t.x_active
                        ? <Badge className="bg-emerald-100 text-emerald-800 border-0">on</Badge>
                        : <Badge variant="secondary">off</Badge>}
                    </td>
                    <td className="px-3 py-2">
                      {t.x_trial_mode
                        ? <Badge className="bg-violet-100 text-violet-800 border-0">trial</Badge>
                        : <Badge variant="secondary">—</Badge>}
                    </td>
                    <td className="px-3 py-2">
                      {t.has_x_bearer_token
                        ? <Badge className="bg-sky-100 text-sky-800 border-0">saved</Badge>
                        : <Badge className="bg-amber-50 text-amber-700 border border-amber-200">missing</Badge>}
                    </td>
                    <td className="px-3 py-2 text-gray-700">{t.x_keywords_count}</td>
                    <td className="px-3 py-2 text-gray-700">{t.x_daily_outreach_cap ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {!data?.env_bearer_token_set && tenants.every((t) => !t.has_x_bearer_token) && (
          <Card className="border-amber-200 bg-amber-50/60">
            <CardContent className="py-3 text-sm text-amber-900 inline-flex gap-2 items-start">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                No tenant has uploaded an X bearer token yet and there&apos;s no
                global fallback in the environment. Polling will be skipped
                for every tenant until at least one bearer is saved.
              </span>
            </CardContent>
          </Card>
        )}
      </div>
    </SaaSAdminLayout>
  );
}

function Stat({ label, value, icon, tone = "slate" }) {
  const toneCls = {
    slate: "bg-slate-50 text-slate-700",
    emerald: "bg-emerald-50 text-emerald-800",
    violet: "bg-violet-50 text-violet-800",
    sky: "bg-sky-50 text-sky-800",
  }[tone];
  return (
    <div className={`rounded-xl border border-gray-100 px-4 py-3 ${toneCls}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70 flex items-center gap-1">
        {icon}{label}
      </div>
      <div className="text-2xl font-bold mt-0.5">{value}</div>
    </div>
  );
}
